import { Link as RouterLink } from "@tanstack/react-router";
import { Clipboard, FileUp, Link as LinkIcon, Mail, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { type IngestJobResponse, api } from "../../lib/api";
import { type IngestJob, JobCard } from "./JobCard";
import { UploadDropzone } from "./UploadDropzone";

const TABS = [
  { id: "files", label: "Archivos", icon: FileUp },
  { id: "paste", label: "Pegar texto", icon: Clipboard },
  { id: "url", label: "Desde URL", icon: LinkIcon },
  { id: "email", label: "Por email", icon: Mail },
];

const TERMINAL_STATUSES = new Set<IngestJob["status"]>(["indexed", "failed", "quarantined"]);

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Error desconocido";
}

function parseJobError(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value) as { message?: string };
    return parsed.message ?? value;
  } catch {
    return value;
  }
}

export function UploadPage() {
  const [tab, setTab] = useState("files");
  const [paste, setPaste] = useState("");
  const [url, setUrl] = useState("");
  const [jobs, setJobs] = useState<IngestJob[]>([]);

  function mapJob(
    response: IngestJobResponse,
    fallback: Pick<IngestJob, "source_type" | "original_filename" | "size_bytes">,
  ): IngestJob {
    return {
      id: response.job_id,
      created_at: response.created_at ?? new Date().toISOString(),
      status: response.status,
      source_type: response.source_type ?? fallback.source_type,
      original_filename: response.original_filename ?? fallback.original_filename,
      size_bytes: response.size_bytes ?? fallback.size_bytes,
      detected_project: response.detected_project ?? undefined,
      detected_lang: response.detected_lang ?? undefined,
      confidence: response.confidence ?? undefined,
      target_doc_id: response.target_doc_id ?? undefined,
      error: parseJobError(response.error_json),
    };
  }

  function upsertJob(job: IngestJob) {
    setJobs((prev) => {
      const without = prev.filter((item) => item.id !== job.id);
      return [job, ...without].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    });
  }

  function replaceTempJob(tempId: string, job: IngestJob) {
    setJobs((prev) => [job, ...prev.filter((item) => item.id !== tempId && item.id !== job.id)]);
  }

  async function pollJob(
    id: string,
    fallback: Pick<IngestJob, "source_type" | "original_filename" | "size_bytes">,
  ) {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const response = await api.getIngestJob(id);
      const job = mapJob(response, fallback);
      upsertJob(job);
      if (TERMINAL_STATUSES.has(job.status)) return;
    }
  }

  async function submitFile(file: File) {
    const tempId = `job_local_${crypto.randomUUID()}`;
    const fallback = {
      source_type: "upload" as const,
      original_filename: file.name,
      size_bytes: file.size,
    };
    upsertJob({
      id: tempId,
      created_at: new Date().toISOString(),
      status: "queued",
      ...fallback,
    });

    try {
      const response = await api.uploadIntakeFile(file);
      const job = mapJob(response, fallback);
      replaceTempJob(tempId, job);
      if (!TERMINAL_STATUSES.has(job.status)) void pollJob(job.id, fallback);
      toast.success(`${file.name} indexado`);
    } catch (err) {
      replaceTempJob(tempId, {
        id: tempId,
        created_at: new Date().toISOString(),
        status: "failed",
        error: errorMessage(err),
        ...fallback,
      });
      toast.error(`No se pudo importar ${file.name}: ${errorMessage(err)}`);
    }
  }

  async function submitPaste() {
    if (!paste.trim()) return;
    const text = paste;
    const fallback = {
      source_type: "paste" as const,
      original_filename: "paste.md",
      size_bytes: text.length,
    };
    const tempId = `job_local_${crypto.randomUUID()}`;
    upsertJob({
      id: tempId,
      created_at: new Date().toISOString(),
      status: "queued",
      ...fallback,
    });
    setPaste("");

    try {
      const response = await api.pasteIntake({ text, filename: "paste.md" });
      const job = mapJob(response, fallback);
      replaceTempJob(tempId, job);
      if (!TERMINAL_STATUSES.has(job.status)) void pollJob(job.id, fallback);
      toast.success("Texto indexado");
    } catch (err) {
      replaceTempJob(tempId, {
        id: tempId,
        created_at: new Date().toISOString(),
        status: "failed",
        error: errorMessage(err),
        ...fallback,
      });
      toast.error(`No se pudo importar el texto: ${errorMessage(err)}`);
    }
  }

  function submitUrl() {
    if (!url.trim()) return;
    toast.error("La importación desde URL aún no está conectada al backend");
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Upload size={20} /> Importar contenido
        </h1>
        <p className="text-sm text-(--text-secondary)">
          Sube documentos, pega texto, importa desde URL o envía por email. DocuStudio convierte a
          Markdown, clasifica e indexa automáticamente.
        </p>
      </header>

      <nav className="mb-4 flex gap-1 border-b border-(--border-subtle)">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
              tab === t.id
                ? "border-(--accent-500) text-(--text-primary) font-medium"
                : "border-transparent text-(--text-secondary) hover:text-(--text-primary)"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <section className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4">
        {tab === "files" && (
          <UploadDropzone
            onFile={(file) => {
              void submitFile(file);
            }}
          />
        )}

        {tab === "paste" && (
          <div className="space-y-3">
            <textarea
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Pega aquí texto, Markdown, HTML o un email…"
              className="h-48 w-full rounded-md border border-(--border-subtle) bg-(--bg-base) px-3 py-2 text-sm"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-(--text-tertiary)">{paste.length} caracteres</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitPaste()}
                disabled={!paste.trim()}
              >
                Procesar
              </Button>
            </div>
          </div>
        )}

        {tab === "url" && (
          <div className="space-y-3">
            <Input
              placeholder="https://ejemplo.com/articulo.html"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <p className="text-xs text-(--text-tertiary)">
              Solo se aceptan dominios en la allowlist. La página se descarga y convierte a
              Markdown.
            </p>
            <Button variant="primary" size="sm" onClick={submitUrl} disabled={!url.trim()}>
              <LinkIcon size={13} /> Descargar y procesar
            </Button>
          </div>
        )}

        {tab === "email" && (
          <div className="space-y-2 text-sm">
            <p>Envía cualquier email a una de estas direcciones:</p>
            <ul className="space-y-1 rounded-md bg-(--bg-base) p-3 font-mono text-xs">
              <li>
                <strong>docs@iort.io</strong> — clasificación automática por contenido
              </li>
              <li>
                <strong>&lt;project&gt;.docs@iort.io</strong> — fuerza proyecto (ej.
                netbox.docs@iort.io)
              </li>
              <li>
                <strong>&lt;user&gt;.apuntes@iort.io</strong> — inbox personal restringido
              </li>
            </ul>
            <p className="text-xs text-(--text-tertiary)">
              Recibirás email de confirmación con el doc generado o, si quedó ambiguo, en tu inbox.
            </p>
            <Button variant="ghost" size="sm" asChild>
              <RouterLink to="/notebooks">Ver inbox →</RouterLink>
            </Button>
          </div>
        )}
      </section>

      {jobs.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold flex items-center gap-2">
            <FileUp size={14} /> Jobs recientes
          </h2>
          <ul className="space-y-2">
            {jobs.map((j) => (
              <li key={j.id}>
                <JobCard job={j} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
